import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getAdminReadingDetail, retryAdminReading } from '../../lib/api';
import type { AdminReadingDetail } from '../../lib/types';
import {
  AdminActionButton,
  AdminBadge,
  AdminCodeBlock,
  AdminEmptyState,
  AdminMetaGrid,
  AdminMetaItem,
  AdminPanel,
  AdminStatCard,
  compactText,
  formatDateTime,
  formatEstimatedLlmCost,
  formatGenerationStatusLabel,
  formatPaymentStatusLabel,
  formatSourceLabel,
  formatSpreadLabel,
  formatTokenCount,
  shortId,
  toneForGeneration,
  toneForPayment,
  toneForSource,
} from './AdminUi';

export default function AdminReadingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [reading, setReading] = useState<AdminReadingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getAdminReadingDetail(id);
      setReading(response.reading);
      setErrorMessage(null);
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '리딩 상세를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [id]);

  const ownerUrl = useMemo(() => {
    if (!reading) return '';
    return `${window.location.origin}/result/${reading.id}`;
  }, [reading]);

  const canRetry = Boolean(
    reading
      && reading.payment_status === 'paid'
      && (reading.generation_status === 'failed' || !reading.result)
  );

  const handleRetry = async () => {
    if (!id || !canRetry || retrying) return;
    setRetrying(true);
    try {
      await retryAdminReading(id);
      await load();
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '리딩 재생성에 실패했습니다.');
    } finally {
      setRetrying(false);
    }
  };

  const handleCopyReplay = async () => {
    if (!ownerUrl) return;
    try {
      await navigator.clipboard.writeText(ownerUrl);
      setCopyMessage('리플레이 링크를 복사했습니다.');
      window.setTimeout(() => setCopyMessage(null), 1800);
    } catch {
      setCopyMessage('클립보드에 접근할 수 없습니다.');
      window.setTimeout(() => setCopyMessage(null), 1800);
    }
  };

  return (
    <div className="space-y-6">
      <AdminPanel
        title={reading ? `리딩 ${shortId(reading.id)}` : '리딩 상세'}
        subtitle={reading ? compactText(reading.question || '저장된 질문이 없습니다.', 120) : '카드, 결과, 결제 연동 상태를 확인합니다'}
        actions={
          <div className="flex flex-wrap gap-2">
            <AdminActionButton to="/admin/readings" label="목록으로" icon="arrow-right" />
            {reading && (
              <Link
                to={`/result/${reading.id}/summary`}
                className="inline-flex items-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-3 py-2 text-sm text-[#f3dfc5] hover:bg-[#2d2019]"
              >
                최종 정리 열기
              </Link>
            )}
          </div>
        }
      >
        {loading ? (
          <p className="text-sm text-[#d8bfa1]/75">리딩 상세를 불러오고 있습니다...</p>
        ) : errorMessage || !reading ? (
          <AdminEmptyState title="리딩 상세를 불러올 수 없습니다" body={errorMessage || '리딩을 찾을 수 없습니다.'} />
        ) : (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <AdminStatCard label="결제 상태" value={formatPaymentStatusLabel(reading.payment_status)} />
              <AdminStatCard label="생성 상태" value={formatGenerationStatusLabel(reading.generation_status)} />
              <AdminStatCard label="응답 출처" value={formatSourceLabel(reading.llm_source)} />
              <AdminStatCard label="재시도 횟수" value={reading.retry_count} />
              <AdminStatCard label="심화 해설 수" value={reading.insight_section_count} caption={`${reading.result_length}자`} />
              <AdminStatCard
                label="LLM 비용"
                value={formatEstimatedLlmCost(reading.llm_estimated_cost_usd)}
                caption={reading.llm_usage ? `${formatTokenCount(reading.llm_usage.totalTokens)} tokens` : 'usage 없음'}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <AdminBadge label={formatSpreadLabel(reading.spread)} tone="muted" />
              <AdminBadge label={formatPaymentStatusLabel(reading.payment_status)} tone={toneForPayment(reading.payment_status)} />
              <AdminBadge label={formatGenerationStatusLabel(reading.generation_status)} tone={toneForGeneration(reading.generation_status)} />
              <AdminBadge label={formatSourceLabel(reading.llm_source)} tone={toneForSource(reading.llm_source)} />
              {reading.llm_model && <AdminBadge label={reading.llm_model} tone="accent" />}
              {reading.expired && <AdminBadge label="만료됨" tone="danger" />}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleCopyReplay}
                className="border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm text-[#f3dfc5] hover:bg-[#2d2019]"
              >
                리플레이 링크 복사
              </button>
              <a
                href={ownerUrl}
                target="_blank"
                rel="noreferrer"
                className="border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm text-[#f3dfc5] hover:bg-[#2d2019]"
              >
                리더 화면 열기
              </a>
              <button
                type="button"
                onClick={handleRetry}
                disabled={!canRetry || retrying}
                className="border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm text-[#f3dfc5] hover:bg-[#2d2019] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {retrying ? '재생성 중...' : '리딩 재생성'}
              </button>
              {copyMessage && <p className="self-center text-sm text-[#d8bfa1]/75">{copyMessage}</p>}
            </div>

            <AdminMetaGrid>
              <AdminMetaItem label="생성 시각" value={formatDateTime(reading.created_at)} />
              <AdminMetaItem label="수정 시각" value={formatDateTime(reading.updated_at)} />
              <AdminMetaItem label="사용자 이메일" value={reading.user_email || '게스트'} />
              <AdminMetaItem label="사용자 ID" value={reading.user_id || '-'} />
              <AdminMetaItem label="주제" value={reading.topic} />
              <AdminMetaItem label="거래 ID" value={reading.transaction_id || '-'} />
              <AdminMetaItem label="이용 가능 시각" value={formatDateTime(reading.accessible_until)} />
              <AdminMetaItem label="결과 길이" value={reading.result_length} />
              <AdminMetaItem label="LLM 총 토큰" value={formatTokenCount(reading.llm_usage?.totalTokens)} />
              <AdminMetaItem label="LLM 입력 토큰" value={formatTokenCount(reading.llm_usage?.promptTokens)} />
              <AdminMetaItem label="LLM 출력 토큰" value={formatTokenCount(reading.llm_usage?.completionTokens)} />
              <AdminMetaItem label="LLM 추론 토큰" value={formatTokenCount(reading.llm_usage?.reasoningTokens)} />
              <AdminMetaItem label="LLM 추정 비용" value={formatEstimatedLlmCost(reading.llm_estimated_cost_usd)} />
            </AdminMetaGrid>
          </div>
        )}
      </AdminPanel>

      {!loading && reading && (
        <>
          <AdminPanel title="카드 정보" subtitle="저장된 카드와 정·역방향 정보">
            {reading.cards && reading.cards.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {reading.cards.map((drawnCard, index) => (
                  <div key={`${drawnCard.card.id}-${index}`} className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">{index + 1}번째 카드</p>
                    <p className="mt-2 text-lg font-semibold text-[#f4e2cb]">{drawnCard.card.name_kr}</p>
                    <p className="mt-1 text-sm text-[#d8bfa1]/72">
                      {drawnCard.orientation === 'upright' ? '정방향' : '역방향'}
                      {drawnCard.positionMeaning ? ` · ${drawnCard.positionMeaning}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <AdminEmptyState title="저장된 카드가 없습니다" body="결제와 카드 뽑기가 끝난 뒤에 카드 정보가 저장됩니다." />
            )}
          </AdminPanel>

          <AdminPanel title="결과 미리보기" subtitle="저장된 리딩 결과를 사람이 읽기 쉬운 형태로 보여줍니다">
            {reading.result ? (
              <div className="space-y-4">
                {reading.result.opening && (
                  <div className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">오프닝</p>
                    <p className="mt-2 text-sm leading-7 text-[#f4e2cb]">{reading.result.opening}</p>
                  </div>
                )}
                <div className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">요약</p>
                  <p className="mt-2 text-sm leading-7 text-[#f4e2cb]">{reading.result.summary}</p>
                </div>
                <div className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">상세 해설</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-[#f4e2cb]">{reading.result.detail}</p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">조언</p>
                    <p className="mt-2 text-sm leading-7 text-[#f4e2cb]">{reading.result.advice}</p>
                  </div>
                  <div className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">주의할 점</p>
                    <p className="mt-2 text-sm leading-7 text-[#f4e2cb]">{reading.result.caution}</p>
                  </div>
                  <div className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">마무리</p>
                    <p className="mt-2 text-sm leading-7 text-[#f4e2cb]">{reading.result.closing || '-'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <AdminEmptyState title="저장된 결과가 없습니다" body="이 리딩에는 아직 저장된 결과가 없습니다." />
            )}
          </AdminPanel>

          <AdminPanel title="원본 데이터" subtitle="정확한 저장값 확인이 필요할 때 사용합니다">
            <div className="space-y-4">
              <details className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                <summary className="cursor-pointer text-sm font-medium text-[#f4e2cb]">카드 JSON</summary>
                <div className="mt-4">
                  <AdminCodeBlock value={JSON.stringify(reading.cards || null, null, 2)} />
                </div>
              </details>
              <details className="border border-[#8a6a4b]/20 bg-black/10 p-4">
                <summary className="cursor-pointer text-sm font-medium text-[#f4e2cb]">결과 JSON</summary>
                <div className="mt-4">
                  <AdminCodeBlock value={JSON.stringify(reading.result || null, null, 2)} />
                </div>
              </details>
            </div>
          </AdminPanel>
        </>
      )}
    </div>
  );
}
